import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PartyPopper, Download, History, X } from "lucide-react";

/**
 * Fires once after the user's FIRST successful single-slide or deck
 * generation. Listens for the `onboarding:first-complete` window event
 * which `PPTGeneratorPage` dispatches when:
 *   - `onboarding_status` was "pending" at the time generation started
 *   - the generation completes successfully
 *
 * The page is responsible for calling `/api/me/onboarding-complete` so
 * this only ever fires once per user.
 */
export default function FirstDeckCelebration() {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState({ kind: "slide", id: null });
  const navigate = useNavigate();

  useEffect(() => {
    const onFirstComplete = (e) => {
      setDetail({
        kind: e?.detail?.kind || "slide",
        id: e?.detail?.id || null,
      });
      setOpen(true);
    };
    window.addEventListener("onboarding:first-complete", onFirstComplete);
    return () => window.removeEventListener("onboarding:first-complete", onFirstComplete);
  }, []);

  if (!open) return null;

  const isDeck = detail.kind === "deck";
  const title = isDeck ? "Your first deck is ready!" : "Your first slide is ready!";
  const subtitle = isDeck
    ? "You just generated a full multi-slide PowerPoint with AI. Download it below or head to History to revisit any time."
    : "You just generated a polished slide with AI. Download it below or jump back into PPT Studio to try a full deck.";

  const handleDownload = () => {
    if (!detail.id) return;
    const path = isDeck ? `/details/deck/${detail.id}` : `/details/presentation/${detail.id}`;
    setOpen(false);
    navigate(path);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={() => setOpen(false)}
      data-testid="first-deck-celebration"
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Confetti-style top band — pure CSS, no library needed */}
        <div className="relative h-32 bg-gradient-to-br from-teal-400 via-teal-500 to-emerald-600 overflow-hidden">
          {/* Decorative dots — staggered for a confetti vibe */}
          <span className="absolute top-4 left-6 w-2 h-2 rounded-full bg-yellow-300 animate-bounce" />
          <span className="absolute top-10 left-20 w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          <span className="absolute top-6 right-12 w-2 h-2 rounded-full bg-pink-300 animate-bounce" style={{ animationDelay: "0.2s" }} />
          <span className="absolute top-16 right-24 w-1.5 h-1.5 rounded-full bg-amber-200 animate-pulse" style={{ animationDelay: "0.4s" }} />
          <span className="absolute top-20 left-12 w-1 h-1 rounded-full bg-white animate-pulse" style={{ animationDelay: "0.6s" }} />
          <span className="absolute top-3 right-32 w-1 h-1 rounded-full bg-yellow-200 animate-bounce" style={{ animationDelay: "0.8s" }} />
          <button
            onClick={() => setOpen(false)}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
            data-testid="celebration-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-white rounded-full p-4 shadow-lg">
              <PartyPopper className="w-10 h-10 text-teal-500" />
            </div>
          </div>
        </div>

        <div className="px-7 pt-6 pb-6 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2" data-testid="celebration-title">
            {title}
          </h2>
          <p className="text-sm text-gray-600 leading-relaxed mb-5">
            {subtitle}
          </p>
          <div className="flex flex-col gap-2">
            <Button
              onClick={handleDownload}
              disabled={!detail.id}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white h-10"
              data-testid="celebration-view-result"
            >
              <Download className="w-4 h-4 mr-2" />
              {isDeck ? "Open & download deck" : "Open & download slide"}
            </Button>
            <Button
              variant="outline"
              onClick={() => { setOpen(false); navigate("/history"); }}
              className="w-full h-10"
              data-testid="celebration-view-history"
            >
              <History className="w-4 h-4 mr-2" />
              View History
            </Button>
            <button
              onClick={() => setOpen(false)}
              className="text-xs text-gray-400 hover:text-gray-600 mt-1.5 transition-colors"
              data-testid="celebration-skip"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
